import type { HoldingDto } from '../shared/types';
import Decimal from 'decimal.js-light';
import type { DisplayCurrency } from './format';

export type HoldingPresentationGroup = 'TH_EQUITY' | 'US_EQUITY' | 'FOREIGN_CURRENCY' | 'THB_CASH' | 'GOLD' | 'CRYPTO';

export function holdingPresentationGroup(instrument: HoldingDto['instrument']): HoldingPresentationGroup {
  if (instrument.kind === 'FIAT') return instrument.symbol.toUpperCase() === 'THB' ? 'THB_CASH' : 'FOREIGN_CURRENCY';
  if (instrument.kind === 'ETF') {
    return ['SET', 'MAI'].includes(instrument.market?.toUpperCase() || '') || instrument.quoteCurrency === 'THB'
      ? 'TH_EQUITY' : 'US_EQUITY';
  }
  return instrument.kind;
}

export function aggregateHoldings(holdings: HoldingDto[], totalValueUsd: string): HoldingDto[] {
  const byInstrument = new Map<string, HoldingDto[]>();
  for (const holding of holdings) byInstrument.set(holding.instrumentId, [...(byInstrument.get(holding.instrumentId) || []), holding]);
  return [...byInstrument.values()].map((rows) => {
    const sum = (field: 'quantity' | 'bookCostUsd' | 'marketValueUsd' | 'unrealizedPlUsd' | 'realizedPlUsd' | 'incomeUsd') =>
      rows.reduce((total, row) => total.add(row[field]), new Decimal(0));
    const quantity = sum('quantity');
    const cost = sum('bookCostUsd');
    const value = sum('marketValueUsd');
    const completeFiatCost = rows[0].instrument.kind !== 'FIAT'
      || rows.every((row) => Object.keys(row.bookCosts).length > 0 && Object.keys(row.averagePurchasePrices).length > 0);
    const native = (field: 'bookCosts' | 'grossCosts' | 'feeCosts' | 'taxCosts' | 'discountCosts' | 'averagePurchasePrices') => {
      if (!completeFiatCost) return {};
      const amounts = new Map<string, Decimal>();
      for (const row of rows) for (const [currency, amount] of Object.entries(row[field])) {
        const part = new Decimal(amount).mul(field === 'averagePurchasePrices' ? row.quantity : 1);
        amounts.set(currency, (amounts.get(currency) || new Decimal(0)).add(part));
      }
      return Object.fromEntries([...amounts].map(([currency, amount]) => [currency,
        field === 'averagePurchasePrices' ? (quantity.isZero() ? '0' : amount.div(quantity).toFixed()) : amount.toFixed(),
      ]));
    };
    return { ...rows[0], quantity: quantity.toFixed(), bookCostUsd: cost.toFixed(), marketValueUsd: value.toFixed(),
      averageCostUsd: quantity.isZero() ? '0' : cost.div(quantity).toFixed(),
      bookCosts: native('bookCosts'), grossCosts: native('grossCosts'), feeCosts: native('feeCosts'), taxCosts: native('taxCosts'),
      discountCosts: native('discountCosts'),
      averagePurchasePrices: native('averagePurchasePrices'),
      unrealizedPlUsd: sum('unrealizedPlUsd').toFixed(), realizedPlUsd: sum('realizedPlUsd').toFixed(), incomeUsd: sum('incomeUsd').toFixed(),
      unrealizedPlPct: rows[0].currentPrice && cost.isPositive() ? sum('unrealizedPlUsd').div(cost).mul(100).toFixed() : null,
      allocationPct: new Decimal(totalValueUsd).isZero() ? 0 : value.div(totalValueUsd).mul(100).toNumber(),
    };
  });
}

export function holdingDisplayValues(holding: HoldingDto, currency: DisplayCurrency, usdThb: string) {
  const convert = (amount: string, source: string) => source === currency ? new Decimal(amount)
    : currency === 'THB' ? new Decimal(amount).mul(usdThb) : new Decimal(amount).div(usdThb);
  const nativeTotal = (amounts: Record<string, string>, fallbackUsd: string) => Object.keys(amounts).length
    ? Object.entries(amounts).reduce((sum, [source, amount]) => sum.add(convert(amount, source)), new Decimal(0))
    : convert(fallbackUsd, 'USD');
  const bookCost = nativeTotal(holding.bookCosts, holding.bookCostUsd);
  const grossCost = nativeTotal(holding.grossCosts, holding.bookCostUsd);
  const feeCost = nativeTotal(holding.feeCosts, '0');
  const taxCost = nativeTotal(holding.taxCosts, '0');
  const discountCost = nativeTotal(holding.discountCosts, '0');
  const averagePurchasePrice = nativeTotal(holding.averagePurchasePrices, holding.averageCostUsd);
  const currentPrice = holding.currentPrice ? convert(holding.currentPrice, holding.quoteCurrency) : null;
  const marketValue = currentPrice?.mul(holding.quantity) || null;
  const profit = marketValue?.sub(bookCost) || null;
  const costBasisAvailable = holding.instrument.kind !== 'FIAT'
    || (Object.keys(holding.bookCosts).length > 0 && Object.keys(holding.averagePurchasePrices).length > 0);
  return {
    costBasisAvailable,
    bookCost: bookCost.toFixed(), grossCost: grossCost.toFixed(), feeCost: feeCost.toFixed(), taxCost: taxCost.toFixed(),
    discountCost: discountCost.toFixed(),
    averagePurchasePrice: averagePurchasePrice.toFixed(),
    currentPrice: currentPrice?.toFixed() || null, marketValue: marketValue?.toFixed() || null,
    unrealizedPl: profit?.toFixed() || null,
    unrealizedPlPct: costBasisAvailable && profit && bookCost.isPositive() ? profit.div(bookCost).mul(100).toFixed() : null,
  };
}

type ReturnHolding = Pick<HoldingDto, 'currentPrice' | 'unrealizedPlPct'>;

export function sortHoldingsByReturn<T extends ReturnHolding>(holdings: T[]): T[] {
  const percent = (holding: ReturnHolding) => {
    if (!holding.currentPrice || holding.unrealizedPlPct === null) return null;
    const value = Number(holding.unrealizedPlPct);
    return Number.isFinite(value) ? value : null;
  };
  return [...holdings].sort((a, b) => {
    const aPct = percent(a);
    const bPct = percent(b);
    if (aPct === null) return bPct === null ? 0 : 1;
    if (bPct === null) return -1;
    return bPct - aPct;
  });
}
